// Payment service - handles communication with Cloudflare Worker
const PAYMENT_API_URL = process.env.REACT_APP_PAYMENT_API_URL || 'http://localhost:8787';

const MONTHLY_DISCOUNT = 0.15;
const ANNUAL_DISCOUNT = 0.3;

function formatUsdFromCents(amountInCents) {
  const amountInDollars = amountInCents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amountInDollars) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amountInDollars);
}

function getDiscountedMonthlyAmount(monthlyAmountInCents) {
  return Math.round(monthlyAmountInCents * (1 - MONTHLY_DISCOUNT));
}

function getDiscountedYearlyAmount(monthlyAmountInCents) {
  return Math.round(monthlyAmountInCents * 12 * (1 - ANNUAL_DISCOUNT));
}

function getFullYearlyAmount(monthlyAmountInCents) {
  return monthlyAmountInCents * 12;
}

const BASIC_MONTHLY_AMOUNT = 2400;
const PRO_MONTHLY_AMOUNT = 6900;

// Pricing configuration (matches Cloudflare Worker)
export const PRICING_PLANS = {
  PRO: {
    id: 'pro',
    name: 'Pro',
    monthly: {
      price: formatUsdFromCents(getDiscountedMonthlyAmount(PRO_MONTHLY_AMOUNT)),
      amount: getDiscountedMonthlyAmount(PRO_MONTHLY_AMOUNT),
    },
    yearly: {
      price: formatUsdFromCents(getDiscountedYearlyAmount(PRO_MONTHLY_AMOUNT)),
      amount: getDiscountedYearlyAmount(PRO_MONTHLY_AMOUNT),
    },
    fullMonthly: {
      price: formatUsdFromCents(PRO_MONTHLY_AMOUNT),
      amount: PRO_MONTHLY_AMOUNT,
    },
    fullYearly: {
      price: formatUsdFromCents(getFullYearlyAmount(PRO_MONTHLY_AMOUNT)),
      amount: getFullYearlyAmount(PRO_MONTHLY_AMOUNT),
    },
    features: ['Near real-time sync (<5 min)', 'Unlimited integerations', '25,000 active items in sync', 'Custom schema support', 'Dedicated account lead'],
  },
  BESTSELLER: {
    id: 'bestseller',
    name: 'Basic',
    monthly: {
      price: formatUsdFromCents(getDiscountedMonthlyAmount(BASIC_MONTHLY_AMOUNT)),
      amount: getDiscountedMonthlyAmount(BASIC_MONTHLY_AMOUNT),
    },
    yearly: {
      price: formatUsdFromCents(getDiscountedYearlyAmount(BASIC_MONTHLY_AMOUNT)),
      amount: getDiscountedYearlyAmount(BASIC_MONTHLY_AMOUNT),
    },
    fullMonthly: {
      price: formatUsdFromCents(BASIC_MONTHLY_AMOUNT),
      amount: BASIC_MONTHLY_AMOUNT,
    },
    fullYearly: {
      price: formatUsdFromCents(getFullYearlyAmount(BASIC_MONTHLY_AMOUNT)),
      amount: getFullYearlyAmount(BASIC_MONTHLY_AMOUNT),
    },
    features: ['5 min sync interval', '5 active integerations', '2,500 active sync items', 'Priority support'],
  },
};

/**
 * Creates a checkout session for a specific plan via Cloudflare Worker
 * @param {string} planId - The plan ID ('pro' or 'bestseller')
 * @param {string} billingCycle - 'monthly' or 'yearly'
 * @param {object} customer - Customer information
 * @param {string} returnUrl - URL to redirect after payment
 * @returns {Promise<object>} - Checkout session response
 */
export async function createCheckoutSession(planId, billingCycle, customer, returnUrl) {
  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        planId,
        billingCycle,
        customer,
        returnUrl,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to create checkout session');
    }

    return result;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

const paymentService = {
  createCheckoutSession,
  PRICING_PLANS,
};

export default paymentService;